import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw, ShieldAlert } from 'lucide-react';
import { Banner } from '../../components/Banner.tsx';
import { Button } from '../../components/Button.tsx';
import { buttonClass } from '../../components/buttonClass.ts';
import { Disclosure } from '../../components/Disclosure.tsx';
import { TextField } from '../../components/TextField.tsx';
import { SelectField } from '../../components/SelectField.tsx';
import { EmptyState } from '../../components/EmptyState.tsx';
import { FindingRow } from '../../components/FindingRow.tsx';
import { Inspector, type InspectorKind } from '../../components/Inspector.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { formatDateTime } from '../../components/format.ts';
import { useCan } from '../../features/auth/permissions.ts';
import { readOwner } from '../../features/authorization/model.ts';
import { authorizedDomainScope } from '../../features/authorization/scope.ts';
import {
  CLAIM_ORIGINS,
  evidenceToInspectorDetail,
  filterAndSortFindings,
  findingToInspectorDetail,
  preserveFindingsListSearch,
  REMEDIATION_STATES,
  RETEST_OUTCOMES,
  REVIEW_STATES,
  FINDING_SEVERITIES,
  selectFindingsCoverageSummary,
  selectWorkspaceFindingCounts,
} from '../../features/findings/model.ts';
import { isAttemptActive } from '../../features/discovery/model.ts';
import { findingTaskLane } from '../../features/discovery/evidenceSummary.ts';
import { useCreateEngagement } from '../../features/engagement/useCreateEngagement.ts';
import { useWorkspaceFindings } from '../../features/findings/store.ts';
import type {
  Finding,
  FindingFilterCriteria,
  FindingSortOption,
  FindingsRouteSearch,
} from '../../features/findings/types.ts';
import { useSession } from '../../state/session.ts';
import styles from './FindingsPage.module.css';

export function FindingsPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as FindingsRouteSearch;

  const workspace = useSession((s) => s.workspace);

  const {
    findings,
    attempt,
    evidence,
    sourceRecords,
    findingEngagementLinks,
    loading,
    loaded,
    stale,
    error,
    loadFinding,
    loadLatestAttemptFindings,
  } = useWorkspaceFindings();
  const createEngagement = useCreateEngagement();
  const canWrite = useCan('workspace.write');

  const workspaceId = workspace?.id;
  const ownerToken = workspace?.authorizationId ? readOwner(workspace.authorizationId) : '';
  const setupSessionId = workspace?.setupSessionId ?? '';
  const readToken = ownerToken;
  const credentials = useMemo(
    () => ({ session: setupSessionId, token: readToken }),
    [setupSessionId, readToken],
  );
  const laneFilter: { lane?: 'DNS' | 'Network' | 'Mixed' } =
    search.lane === 'DNS' || search.lane === 'Network' || search.lane === 'Mixed'
      ? { lane: search.lane }
      : {};

  useEffect(() => {
    if (!workspaceId) return;
    void loadLatestAttemptFindings(workspaceId, credentials, laneFilter);
  }, [workspaceId, credentials, loadLatestAttemptFindings, search.lane]);

  const attemptActive = isAttemptActive(attempt ?? null);
  useEffect(() => {
    if (!workspaceId || !attemptActive) return;
    const timer = window.setTimeout(() => {
      void loadLatestAttemptFindings(workspaceId, credentials, laneFilter);
    }, 10_000);
    return () => window.clearTimeout(timer);
  }, [workspaceId, credentials, attemptActive, findings, loadLatestAttemptFindings, search.lane]);

  // Search input local state synced with URL without useEffect setState
  const [queryInput, setQueryInput] = useState(search.q ?? '');
  const [prevSearchQ, setPrevSearchQ] = useState(search.q);
  if (search.q !== prevSearchQ) {
    setPrevSearchQ(search.q);
    setQueryInput(search.q ?? '');
  }

  const updateSearch = useCallback(
    (patch: Partial<FindingsRouteSearch>) => {
      const next = { ...search, ...patch };
      // Clean up undefined / empty / ALL values
      const cleaned: Record<string, string> = {};
      for (const [key, val] of Object.entries(next)) {
        if (val !== undefined && val !== '' && val !== 'ALL') {
          cleaned[key] = val;
        }
      }
      void navigate({
        to: '/findings',
        search: cleaned,
        replace: true,
      });
    },
    [navigate, search],
  );

  const handleQueryChange = (val: string) => {
    setQueryInput(val);
    updateSearch({ q: val || undefined });
  };

  const handleClearAllFilters = () => {
    setQueryInput('');
    void navigate({
      to: '/findings',
      search: {},
      replace: true,
    });
  };

  // Derived filter criteria from queryInput (immediate feedback) and URL search params
  const filterCriteria: FindingFilterCriteria = useMemo(
    () => ({
      query: queryInput,
      severity: search.severity as Finding['normalizedSeverity'] | 'ALL',
      origin: search.origin as Finding['claimOrigin'] | 'ALL',
      review: search.review as Finding['analystReviewState'] | 'ALL',
      remediation: search.remediation as Finding['remediationState'] | 'ALL',
      retest: search.retest as Finding['retestOutcome'] | 'ALL',
      asset: search.asset as string | 'ALL',
      lane: search.lane as FindingFilterCriteria['lane'],
    }),
    [queryInput, search],
  );

  const sortOption: FindingSortOption = (search.sort as FindingSortOption) || 'severity-desc';

  // Compute counts across all loaded findings
  const allCounts = useMemo(() => selectWorkspaceFindingCounts(findings), [findings]);

  // Compute filtered & sorted findings
  const filteredFindings = useMemo(
    () => filterAndSortFindings(findings, filterCriteria, sortOption, evidence),
    [findings, filterCriteria, sortOption, evidence],
  );
  const taskLaneActive =
    filterCriteria.lane === 'DNS' ||
    filterCriteria.lane === 'Network' ||
    filterCriteria.lane === 'Mixed';

  const coverageSummary = useMemo(
    () => selectFindingsCoverageSummary(attempt, findings.length),
    [attempt, findings.length],
  );
  const coverageDetails = coverageSummary.totalUnits
    ? `${coverageSummary.completedUnits} of ${coverageSummary.totalUnits} coverage units completed; ${coverageSummary.partialUnits} partial, ${coverageSummary.failedUnits} failed, ${coverageSummary.skippedUnits} skipped, and ${coverageSummary.pendingUnits} pending.`
    : 'The service has not returned coverage details for this attempt.';

  // Inspector selection handling (supports both URL search state and direct local interaction)
  const [selectedInspector, setSelectedInspector] = useState<{
    kind: 'finding' | 'evidence';
    id: string;
  } | null>(() => {
    if (search.inspector && search.id) {
      return { kind: search.inspector as 'finding' | 'evidence', id: search.id };
    }
    return null;
  });

  const activeInspector = useMemo(() => {
    return (
      selectedInspector ||
      (search.inspector && search.id
        ? { kind: search.inspector as 'finding' | 'evidence', id: search.id }
        : null)
    );
  }, [selectedInspector, search.inspector, search.id]);
  const inspectorOpen = Boolean(activeInspector);

  const inspectedFinding = useMemo(() => {
    if (activeInspector?.kind === 'finding') {
      return findings.find((f) => f.findingId === activeInspector.id) ?? null;
    }
    return null;
  }, [findings, activeInspector]);

  const inspectedEvidence = useMemo(() => {
    if (activeInspector?.kind === 'evidence') {
      const ev = evidence.find((e) => e.evidenceId === activeInspector.id);
      if (ev) {
        const src = sourceRecords.find((s) => s.sourceRecordId === ev.sourceRecordId);
        return evidenceToInspectorDetail(ev, src, null, findings);
      }
    }
    return null;
  }, [evidence, sourceRecords, findings, activeInspector]);

  const handleInspectFinding = (findingId: string) => {
    setSelectedInspector({ kind: 'finding', id: findingId });
    updateSearch({ inspector: 'finding', id: findingId, selected: findingId });
    const finding = findings.find((item) => item.findingId === findingId);
    if (
      workspaceId &&
      finding?.evidenceIds.some((id) => !evidence.some((item) => item.evidenceId === id))
    ) {
      void loadFinding(workspaceId, findingId, credentials);
    }
  };

  const handleSelectFinding = (findingId: string) => {
    void navigate({
      to: '/findings/$findingId',
      params: { findingId },
      search: preserveFindingsListSearch(search, findingId),
    });
  };

  const refreshFindings = useCallback(() => {
    if (workspaceId) void loadLatestAttemptFindings(workspaceId, credentials, laneFilter);
  }, [workspaceId, credentials, loadLatestAttemptFindings, search.lane]);

  const handleCloseInspector = () => {
    setSelectedInspector(null);
    updateSearch({ inspector: undefined, id: undefined });
  };

  // Detect whether any filters are active
  const hasActiveFilters = Boolean(
    search.q ||
    (search.severity && search.severity !== 'ALL') ||
    (search.origin && search.origin !== 'ALL') ||
    (search.review && search.review !== 'ALL') ||
    (search.remediation && search.remediation !== 'ALL') ||
    (search.retest && search.retest !== 'ALL') ||
    (search.asset && search.asset !== 'ALL') ||
    (search.lane && search.lane !== 'ALL'),
  );

  // Responsive inspector presentation mode
  const [inspectorMode, setInspectorMode] = useState<'dock' | 'drawer' | 'full'>('dock');
  useEffect(() => {
    const updateMode = () => {
      const w = window.innerWidth;
      if (w >= 1100) setInspectorMode('dock');
      else if (w >= 1024) setInspectorMode('drawer');
      else setInspectorMode('full');
    };
    updateMode();
    window.addEventListener('resize', updateMode);
    return () => window.removeEventListener('resize', updateMode);
  }, []);

  const attemptAt = attempt?.startedAt ?? attempt?.start?.requestedAt;
  const scopeDomains = attempt?.authority?.scopeDomains ?? [];
  const coverageIncomplete = coverageSummary.kind === 'partial';
  const reviewLabel = (rev: (typeof REVIEW_STATES)[number]) =>
    rev === 'UNREVIEWED'
      ? 'Unreviewed'
      : rev === 'NEEDS_EVIDENCE'
        ? 'Needs evidence'
        : rev === 'ANALYST_VERIFIED'
          ? 'Analyst-verified'
          : 'Analyst-rejected';

  return (
    <div className={styles.page} data-workbench>
      <div className={styles.headingRow}>
        <header className={styles.header}>
          <h1 className={styles.title}>Findings</h1>
        </header>
        <div className={styles.headingMeta}>
          {attemptAt && (
            <span>
              Latest attempt ·{' '}
              <time dateTime={attemptAt} title={attemptAt}>
                {formatDateTime(attemptAt)}
              </time>
            </span>
          )}
          <Button
            variant="tertiary"
            size="small"
            className={styles.refreshButton}
            onPress={refreshFindings}
            isDisabled={!workspaceId || loading}
            aria-label={loading ? 'Refreshing findings' : 'Refresh findings'}
          >
            <RefreshCw size={14} aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* Scope and coverage in one line; the full wording stays in the title. */}
      {attempt &&
        (coverageSummary.kind === 'complete' ||
          coverageSummary.kind === 'partial' ||
          coverageSummary.kind === 'active') && (
          <p
            className={styles.attemptLine}
            data-tone={coverageIncomplete ? 'attention' : undefined}
          >
            <span title={scopeDomains.length ? authorizedDomainScope(scopeDomains) : undefined}>
              <span className={styles.attemptLabel}>Scope</span>{' '}
              <code>{scopeDomains.length ? scopeDomains.join(', ') : 'not recorded'}</code>
            </span>
            {coverageSummary.totalUnits > 0 && (
              <span title={coverageDetails}>
                {coverageIncomplete && <AlertTriangle size={14} aria-hidden="true" />}
                <span className={styles.attemptLabel}>
                  {coverageSummary.kind === 'active'
                    ? 'Discovery in progress'
                    : coverageIncomplete
                      ? 'Coverage incomplete'
                      : 'Coverage'}
                </span>{' '}
                {coverageSummary.completedUnits} of {coverageSummary.totalUnits} units completed
                {coverageSummary.partialUnits > 0 && ` · ${coverageSummary.partialUnits} partial`}
                {coverageSummary.failedUnits > 0 && ` · ${coverageSummary.failedUnits} failed`}
                {coverageSummary.skippedUnits > 0 && ` · ${coverageSummary.skippedUnits} skipped`}
                {coverageSummary.pendingUnits > 0 && ` · ${coverageSummary.pendingUnits} pending`}
              </span>
            )}
          </p>
        )}

      {(coverageSummary.kind === 'failed' || coverageSummary.kind === 'stopped') && (
        <Banner
          tone={coverageSummary.kind === 'failed' ? 'negative' : 'attention'}
          icon="warning"
          title={
            coverageSummary.kind === 'failed'
              ? 'Discovery did not complete'
              : 'Discovery was stopped'
          }
        >
          The empty results below do not establish that the authorized scope had no findings.
        </Banner>
      )}

      {stale && (
        <Banner tone="attention" icon="warning" title="Findings data may be stale">
          We can’t reach Traceira right now. The last confirmed findings are shown.
        </Banner>
      )}

      {/* Filters on an empty collection are noise; they return once records or a filter exist. */}
      {!(loaded && findings.length === 0 && !hasActiveFilters) && (
        <section className={styles.toolbar} aria-label="Findings filters and search">
          <div className={styles.toolbarRow}>
            <div className={styles.searchField}>
              <TextField
                label="Search findings"
                value={queryInput}
                onChange={handleQueryChange}
                placeholder="Search title, asset, or ID"
                hideLabel
              />
            </div>
            <SelectField
              inline
              label="Severity"
              value={search.severity || 'ALL'}
              onChange={(severity) => updateSearch({ severity })}
              options={[
                ['ALL', 'All'],
                ...FINDING_SEVERITIES.map(
                  (sev) =>
                    [
                      sev,
                      `${sev.charAt(0) + sev.slice(1).toLowerCase()} (${allCounts.severity[sev]})`,
                    ] as const,
                ),
              ]}
            />
            <SelectField
              inline
              label="Task"
              value={search.lane || 'ALL'}
              onChange={(lane) => updateSearch({ lane })}
              options={[
                ['ALL', 'All'],
                ['DNS', 'DNS'],
                ['Network', 'Network'],
                ['Mixed', 'Mixed'],
              ]}
            />
            <SelectField
              inline
              label="Review"
              value={search.review || 'ALL'}
              onChange={(review) => updateSearch({ review })}
              options={[
                ['ALL', 'All'],
                ...REVIEW_STATES.map((rev) => [rev, reviewLabel(rev)] as const),
              ]}
            />
            <SelectField
              inline
              label="Origin"
              value={search.origin || 'ALL'}
              onChange={(origin) => updateSearch({ origin })}
              options={[
                ['ALL', 'All'],
                ...CLAIM_ORIGINS.map(
                  (origin) =>
                    [
                      origin,
                      origin === 'SOURCE_REPORTED'
                        ? 'Source-reported'
                        : origin === 'MODEL_INFERRED'
                          ? 'AI-suggested'
                          : 'Analyst-authored',
                    ] as const,
                ),
              ]}
            />
            <SelectField
              inline
              label="Sort"
              value={sortOption}
              onChange={(sort) => updateSearch({ sort })}
              options={[
                ['severity-desc', 'Severity ↓'],
                ['severity-asc', 'Severity ↑'],
                ['date-desc', 'Newest observed'],
                ['date-asc', 'Oldest observed'],
                ['title-asc', 'Title A–Z'],
                ['title-desc', 'Title Z–A'],
              ]}
            />
            {hasActiveFilters && (
              <button type="button" className={styles.clearFilters} onClick={handleClearAllFilters}>
                Clear filters
              </button>
            )}
            <span className={styles.resultCount} role="status" aria-live="polite">
              {!loaded
                ? ''
                : hasActiveFilters
                  ? `Showing ${filteredFindings.length} of ${findings.length} findings`
                  : `${findings.length} ${findings.length === 1 ? 'finding' : 'findings'}`}
            </span>
          </div>
          <Disclosure
            title="More filters"
            bare
            defaultExpanded={Boolean(search.remediation || search.retest || search.asset)}
          >
            <div className={styles.toolbarPrimary}>
              <div className={styles.filterSelect}>
                <SelectField
                  label="Remediation"
                  value={search.remediation || 'ALL'}
                  onChange={(remediation) => updateSearch({ remediation })}
                  options={[
                    ['ALL', 'All remediation states'],
                    ...REMEDIATION_STATES.map(
                      (rem) =>
                        [
                          rem,
                          rem === 'NOT_STARTED'
                            ? 'Not started'
                            : rem === 'IN_PROGRESS'
                              ? 'In progress'
                              : rem === 'AWAITING_RETEST'
                                ? 'Awaiting re-test'
                                : rem === 'ACCEPTED_RISK'
                                  ? 'Accepted risk'
                                  : 'Remediation complete',
                        ] as const,
                    ),
                  ]}
                />
              </div>
              <div className={styles.filterSelect}>
                <SelectField
                  label="Manual re-test"
                  value={search.retest || 'ALL'}
                  onChange={(retest) => updateSearch({ retest })}
                  options={[
                    ['ALL', 'All re-test outcomes'],
                    ...RETEST_OUTCOMES.map(
                      (rt) =>
                        [
                          rt,
                          rt === 'NOT_RECORDED'
                            ? 'Not recorded'
                            : rt === 'FIXED'
                              ? 'Fixed in recorded re-test'
                              : rt === 'STILL_VULNERABLE'
                                ? 'Still vulnerable'
                                : rt === 'INCONCLUSIVE'
                                  ? 'Inconclusive'
                                  : 'Unable to test',
                        ] as const,
                    ),
                  ]}
                />
              </div>
              <div className={styles.filterSelect}>
                <TextField
                  label="Asset"
                  value={search.asset ?? ''}
                  onChange={(asset) => updateSearch({ asset: asset || undefined })}
                  placeholder="Any asset"
                />
              </div>
            </div>
          </Disclosure>
        </section>
      )}

      {/* Main Content Layout (Table + optional docked Inspector) */}
      <div
        className={styles.contentLayout}
        data-has-inspector={inspectorOpen && inspectorMode === 'dock'}
      >
        <div className={styles.tableContainer}>
          {loading && !loaded && (
            <div className={styles.rowsList} aria-label="Loading findings">
              <Skeleton label="Loading workspace findings…" />
            </div>
          )}

          {error && !loaded && (
            <EmptyState
              title="Findings unavailable"
              description={error.message}
              variant="unavailable"
              action={{
                label: 'Retry',
                onPress: () => {
                  if (workspaceId)
                    void loadLatestAttemptFindings(workspaceId, credentials, laneFilter);
                },
              }}
            />
          )}

          {loaded &&
            findings.length === 0 &&
            taskLaneActive &&
            coverageSummary.kind !== 'not-started' && (
              <EmptyState
                title="No matching findings"
                description={
                  filterCriteria.lane === 'DNS'
                    ? 'No DNS task findings match your current filter and search criteria.'
                    : filterCriteria.lane === 'Network'
                      ? 'No network task findings match your current filter and search criteria.'
                      : 'No mixed DNS/network findings match your current filter and search criteria.'
                }
                variant="filtered-empty"
              />
            )}

          {loaded && findings.length === 0 && coverageSummary.kind === 'not-started' && (
            <EmptyState
              title="No findings yet"
              description="Network and DNS discovery is not available yet. Once enabled, findings will appear here with their supporting evidence, ready for you to choose what to investigate."
              variant="not-started"
              icon={ShieldAlert}
              action={{
                label: 'Return to overview',
                onPress: () => void navigate({ to: '/' }),
              }}
            />
          )}

          {loaded &&
            findings.length === 0 &&
            !taskLaneActive &&
            coverageSummary.kind === 'active' && (
              <EmptyState
                title="No findings recorded yet"
                description="Discovery is still in progress. No finding records have been returned so far."
                variant="neutral"
                action={{
                  label: 'Refresh findings',
                  onPress: refreshFindings,
                  variant: 'secondary',
                }}
              />
            )}

          {loaded &&
            findings.length === 0 &&
            !taskLaneActive &&
            coverageSummary.kind === 'partial' && (
              <EmptyState
                title="No findings in the available results"
                description="Some coverage did not complete. An empty list here does not mean the authorized scope had no findings."
                variant="unavailable"
                untestedScope="Coverage units marked partial, failed, skipped, or pending were not fully covered."
                evidenceLimitations="Only finding records returned by the service are shown."
                action={{
                  label: 'Refresh findings',
                  onPress: refreshFindings,
                  variant: 'secondary',
                }}
              />
            )}

          {loaded &&
            findings.length === 0 &&
            !taskLaneActive &&
            (coverageSummary.kind === 'failed' || coverageSummary.kind === 'stopped') && (
              <EmptyState
                title={
                  coverageSummary.kind === 'failed'
                    ? 'No findings recorded before failure'
                    : 'No findings recorded before stop'
                }
                description="Collection ended before it could establish a complete result for the authorized scope."
                variant="unavailable"
                action={{
                  label: 'Refresh findings',
                  onPress: refreshFindings,
                  variant: 'secondary',
                }}
              />
            )}

          {loaded &&
            findings.length === 0 &&
            !taskLaneActive &&
            coverageSummary.kind === 'complete' && (
              <EmptyState
                title="No findings recorded in the completed checks"
                description="Completed DNS and network discovery checks found no security findings for the authorized scope."
                variant="zero-results"
                untestedScope="Undiscovered subdomains, non-web protocols, and intrusive vulnerability exploits were not tested in this discovery."
                evidenceLimitations="Passive observation and non-destructive checks only; absence of findings does not guarantee absence of vulnerabilities."
                action={{
                  label: 'Return to overview',
                  onPress: () => void navigate({ to: '/' }),
                }}
              />
            )}

          {loaded && findings.length > 0 && filteredFindings.length === 0 && (
            <EmptyState
              title="No matching findings"
              description={
                filterCriteria.lane === 'DNS'
                  ? 'No DNS task findings match your current filter and search criteria.'
                  : filterCriteria.lane === 'Network'
                    ? 'No network task findings match your current filter and search criteria.'
                    : filterCriteria.lane === 'Mixed'
                      ? 'No mixed DNS/network findings match your current filter and search criteria.'
                      : 'No findings match your current filter and search criteria.'
              }
              variant="filtered-empty"
              action={{
                label: 'Clear all filters',
                onPress: handleClearAllFilters,
              }}
            />
          )}

          {loaded && filteredFindings.length > 0 && (
            <>
              <div className={styles.tableHeader} aria-hidden="true">
                <span>Severity</span>
                <span>Finding</span>
                <span>Asset</span>
                <span>Review</span>
                <span>Last observed</span>
                <span />
              </div>
              {createEngagement.error && (
                <Banner tone="negative" title="Could not create the engagement">
                  {createEngagement.error.message}
                </Banner>
              )}
              <div className={styles.rowsList} role="table" aria-label="Workspace findings list">
                {filteredFindings.map((finding) => {
                  const engagementId = findingEngagementLinks.find(
                    (link) =>
                      link.findingId === finding.findingId && link.workspaceId === workspaceId,
                  )?.engagementId;
                  return (
                    <FindingRow
                      key={finding.findingId}
                      findingId={finding.findingId}
                      title={finding.title}
                      severity={finding.normalizedSeverity}
                      asset={finding.affectedAssets?.[0] ?? ''}
                      claimOrigin={finding.claimOrigin}
                      reviewState={finding.analystReviewState}
                      lane={findingTaskLane(finding, evidence)}
                      updatedAt={finding.lastObservedAt}
                      isSelected={
                        search.selected === finding.findingId || search.id === finding.findingId
                      }
                      onSelect={handleSelectFinding}
                      onInspect={handleInspectFinding}
                      action={
                        inspectorOpen && inspectorMode === 'dock' ? undefined : engagementId ? (
                          <Link
                            to="/engagements/$engagementId"
                            params={{ engagementId }}
                            className={buttonClass('tertiary', 'small')}
                            aria-label={`Open engagement for ${finding.title}`}
                          >
                            Open engagement
                          </Link>
                        ) : (
                          canWrite && (
                            <Button
                              variant="tertiary"
                              size="small"
                              isDisabled={createEngagement.pendingId !== null}
                              onPress={() => void createEngagement.create(finding.findingId)}
                              aria-label={`Create engagement for ${finding.title}`}
                            >
                              {createEngagement.pendingId === finding.findingId
                                ? 'Creating…'
                                : 'Create engagement'}
                            </Button>
                          )
                        )
                      }
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Inspector Panel */}
        {inspectorOpen && (
          <div className={styles.inspectorDock}>
            <Inspector
              selection={{
                kind: (activeInspector?.kind as InspectorKind) || 'finding',
                id: activeInspector?.id ?? '',
              }}
              mode={inspectorMode}
              finding={inspectedFinding ? findingToInspectorDetail(inspectedFinding) : null}
              evidence={inspectedEvidence}
              onClose={handleCloseInspector}
              onSelectFinding={(id) =>
                void navigate({ to: '/findings/$findingId', params: { findingId: id } })
              }
              onSelectEvidence={(evidenceId) => {
                setSelectedInspector({ kind: 'evidence', id: evidenceId });
                updateSearch({ inspector: 'evidence', id: evidenceId });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
